import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { useAsyncEffect, useDebounceFn } from 'ahooks';
import { Modal } from 'tea-component';
import { concat } from 'lodash-es';
import { downloadFile } from '@home/utils/file';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useSessionId } from '../../../../ChatPage/hooks/useAgentModalSata';
import { useDownloadNotification } from '../../../../ChatPage/components/DownloadLink/hooks';
import type { IAiGalleryPublishFormValues } from '../components/AiGalleryPublishModal';
import { AI_GALLERY_STEP } from '../constants';

/**
 * 将 COS URL 转换为代理预览/下载 URL
 * 新接口：/api/v1/util/cos_design/:cos_type/*file_path
 *
 * @param cosUrl - 完整的 COS URL 或相对路径（例如 https://bucket.cos.xxx.com/databrain_agent/... 或 databrain_agent/...）
 * @param cosType - COS 配置类型，默认 'agentCos'
 * @returns 代理 URL（例如 /api/v1/util/cos_design/agentCos/databrain_agent/...）
 */
const convertToProxyUrl = (cosUrl: string, cosType: string = 'agentCos'): string => {
  if (!cosUrl) return '';

  let path: string;
  if (cosUrl.startsWith('http://') || cosUrl.startsWith('https://')) {
    // 完整 URL → 提取路径部分（去掉 host 等前缀）
    try {
      path = new URL(cosUrl).pathname;
    } catch {
      return '';
    }
  } else {
    // 相对路径直接使用
    path = cosUrl.startsWith('/') ? cosUrl : `/${cosUrl}`;
  }

  return `/api/v1/util/cos_design/${cosType}${path}`;
};

export interface IUseAiGalleryPanelOptions {
  /** 当前右侧面板数据（来自 artifact value + DocumentCmp 合并，包含 cos_url） */
  dsReport?: Record<string, unknown> | null;
  /** 流式会话状态，未完成时禁用工具操作 */
  streamingState?: string | boolean | null;
}

export interface IUseAiGalleryPanelReturn {
  /** 当前步骤：预览 | 代码 */
  stepActive: typeof AI_GALLERY_STEP.PREVIEW | typeof AI_GALLERY_STEP.CODE;
  /** 切换步骤 */
  setStepActive: (step: typeof AI_GALLERY_STEP.PREVIEW | typeof AI_GALLERY_STEP.CODE) => void;
  /** 预览/下载用 URL（代理 URL，通过后端转发 COS 私有桶文件） */
  previewUrl: string;
  /** 原始 HTML 字符串（已下载解析） */
  htmlSource: string;
  /** 展示用标题 */
  displayTitle: string;
  /** 是否禁用工具栏（流式未完成或加载中） */
  toolbarDisabled: boolean;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 导出按钮：下载 HTML 文件 */
  handleDownload: () => void;
  /** 复制到剪贴板：优先 HTML 源码，否则预览 URL */
  handleCopyClipboard: () => Promise<void>;
  /** 复制成功提示是否显示 */
  showCopyTips: boolean;
  /** 打开发布表单弹窗 */
  handlePublish: () => void;
  /** 发布弹窗是否可见 */
  publishModalVisible: boolean;
  /** 关闭发布弹窗 */
  closePublishModal: () => void;
  /** 提交发布表单 */
  handleConfirmPublish: (values: IAiGalleryPublishFormValues) => void;
  /** 发布成功后的浏览链接弹窗是否可见 */
  shareLinkModalVisible: boolean;
  /** 发布成功后生成的可访问链接 */
  publishedUrl: string;
  /** 发布成功回调：记录链接并打开浏览链接弹窗 */
  handlePublishSuccess: (result: { id: string; rule_id?: number; rule_key?: string; url?: string; }) => void;
  /** 关闭浏览链接弹窗 */
  closeShareLinkModal: () => void;
}

/**
 * AI Gallery 右侧面板状态与交互
 * 
 * 流程：
 * 1. 点击卡片时，dsReport 中包含 cos_url
 * 2. 将 cos_url 转换为代理 URL（新接口：/api/v1/util/cos/:cos_type/*file_path）
 * 3. 使用代理 URL 下载 HTML 内容（后端自动处理鉴权和转发）
 * 4. 预览 Tab 使用 iframe 展示代理 URL
 * 5. 代码 Tab 展示下载的 HTML 源码
 */
export function useAiGalleryPanel(
  options: IUseAiGalleryPanelOptions,
): IUseAiGalleryPanelReturn {
  const { dsReport } = options;
  const { formatMessage } = useIntl();
  const report = (dsReport || {}) as Record<string, unknown>;
  // 复制成功提示状态
  const [showCopyTips, setShowCopyTips] = useState(false);
  // 下载通知（loading → success / error）
  const { setNoticeStatus } = useDownloadNotification();
  // 敏感数据检测（综合 sensitive_data_types 和 has_sensitive_data 判断）
  const { chatList, bookMarkedChats } = useChatList({ manual: true });
  const { sessionId: routeSessionId } = useSessionId();
  const sensitiveConversation = useMemo(() => {
    const chat = concat(chatList, bookMarkedChats).find((item) => item.session_id === routeSessionId);
    if (!chat) return false;
    const chatWithSensitive = chat as typeof chat & {
      sensitive_data_types?: { dashboard?: boolean; mgmt?: boolean; };
      has_sensitive_data?: boolean;
    };
    const { dashboard, mgmt } = chatWithSensitive.sensitive_data_types || {};
    return !!(dashboard || chatWithSensitive.has_sensitive_data || mgmt);
  }, [chatList, bookMarkedChats, routeSessionId]);

  // 从 dsReport 中提取 cos_url
  const cosUrl = (report.cos_url as string) || '';

  const [stepActive, setStepActive] = useState<
    typeof AI_GALLERY_STEP.PREVIEW | typeof AI_GALLERY_STEP.CODE
  >(AI_GALLERY_STEP.PREVIEW);

  /** 发布表单弹窗 */
  const [publishModalVisible, setPublishModalVisible] = useState(false);

  /** 发布成功后的浏览链接弹窗 */
  const [shareLinkModalVisible, setShareLinkModalVisible] = useState(false);
  /** 发布成功后生成的可访问链接 */
  const [publishedUrl, setPublishedUrl] = useState<string>('');

  /** 代理预览 URL（通过后端转发 COS 私有桶文件） */
  const [previewUrl, setPreviewUrl] = useState<string>('');

  /** HTML 源码内容 */
  const [htmlSource, setHtmlSource] = useState<string>('');

  /** 加载状态 */
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** 展示标题 */
  const displayTitle = useMemo(() => {
    const name = typeof report.name === 'string' ? report.name : '';
    const title = typeof report.title === 'string' ? report.title : '';
    return (name || title || formatMessage({ id: 'dba_ai_gallery_panel_title' })) as string;
  }, [report, formatMessage]);

  /** 下载文件名 */
  const downloadFileName = useMemo(() => {
    const baseName = displayTitle || 'ai-gallery';
    return baseName.endsWith('.html') ? baseName : `${baseName}.html`;
  }, [displayTitle]);

  /** 是否禁用工具栏（流式输出期间允许操作） */
  const toolbarDisabled = isLoading;

  /**
   * 下载 HTML 文件内容
   */
  const downloadHtmlContent = useCallback(async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('[AiGallery] 下载 HTML 内容失败:', error);
      return '';
    }
  }, []);

  /**
   * 将 cos_url 转换为代理 URL 并下载内容
   * 新接口：/api/v1/util/cos/:cos_type/*file_path
   */
  const signAndFetchContent = useCallback(async (url: string) => {
    if (!url) {
      setPreviewUrl('');
      setHtmlSource('');
      return;
    }

    setIsLoading(true);
    try {
      // 1. 转换为代理 URL（无需签名，后端会自动处理鉴权和转发）
      const proxyUrl = convertToProxyUrl(url, 'agentCos');
      setPreviewUrl(proxyUrl);

      // 2. 下载 HTML 内容
      if (proxyUrl) {
        const content = await downloadHtmlContent(proxyUrl);
        setHtmlSource(content);
      } else {
        setHtmlSource('');
      }
    } catch (error) {
      console.error('[AiGallery] 转换 URL 或下载失败:', error);
      setPreviewUrl('');
      setHtmlSource('');
    } finally {
      setIsLoading(false);
    }
  }, [downloadHtmlContent]);

  /**
   * 当 cos_url 变化时，自动转换为代理 URL 并获取内容
   */
  useAsyncEffect(async () => {
    if (cosUrl) {
      await signAndFetchContent(cosUrl);
    } else {
      setPreviewUrl('');
      setHtmlSource('');
    }
  }, [cosUrl]);

  /** 切换会话或文件时回到预览 Tab */
  useEffect(() => {
    setStepActive(AI_GALLERY_STEP.PREVIEW);
  }, [cosUrl, report.id]);

  /** 导出下载 HTML 文件（带防抖） */
  const { run: handleDownload } = useDebounceFn(
    async () => {
      if (toolbarDisabled || !htmlSource) return;
      setNoticeStatus('loading');
      const blob = new Blob([htmlSource], { type: 'text/html; charset=utf-8' });
      downloadFile(blob, downloadFileName);
      // 用 setTimeout(0) 将 success 推到宏任务队列，
      // 确保 React 先完成 loading 通知的渲染和 useEffect 执行（destroy 前一个 + 创建 loading 通知），
      // 避免 loading 和 success 通知被批量合并导致同时出现
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      setNoticeStatus('success');
    },
    { wait: 500, leading: true, trailing: false },
  );

  

  /** 复制到剪贴板 */
  const handleCopyClipboard = useCallback(async () => {
    if (toolbarDisabled) return;
    const text = (htmlSource.trim() || previewUrl.trim() || '').trim();
    if (!text) return;
    const success = await copyToClipboard(text);
    if (success) {
      if (showCopyTips) return;
      setShowCopyTips(true);
      const timer = setTimeout(() => {
        setShowCopyTips(false);
        clearTimeout(timer);
      }, 2000);
    }
  }, [toolbarDisabled, htmlSource, previewUrl, showCopyTips]);

  /** 打开发布表单弹窗（含敏感数据检测） */
  const handlePublish = useCallback((): void => {
    if (toolbarDisabled) return;

    // 敏感数据检测：弹出确认弹窗
    if (sensitiveConversation) {
      void Modal.confirm({
        caption: formatMessage({ id: 'dba_menu_sensitive_data_detected' }),
        message: formatMessage({ id: 'dba_ai_gallery_publish_sensitive_tip' }),
        okText: formatMessage({ id: 'dba_ai_gallery_publish_still_publish' }),
        cancelText: formatMessage({ id: 'dba_cancel' }),
        className: 'dba-confirm-modal dba-confirm-modal--sensitive',
      }).then((confirmed) => {
        if (confirmed) {
          setPublishModalVisible(true);
        }
      });
      return;
    }

    setPublishModalVisible(true);
  }, [toolbarDisabled, sensitiveConversation, formatMessage]);

  /** 关闭发布表单弹窗 */
  const closePublishModal = useCallback((): void => {
    setPublishModalVisible(false);
  }, []);

  /** 确认发布 */
  const handleConfirmPublish = useCallback(
    (_values: IAiGalleryPublishFormValues): void => {
      setPublishModalVisible(false);
    },
    [],
  );

  /**
   * 「全屏」按钮打开的链接就是 previewUrl（COS 代理 URL），
   * 发布成功后分享链接也用同一个 URL。
   *
   * 后端返回结构：{ id, rule_id, rule_key, url }
   * 优先用后端直接返回的 url，否则用当前面板的 previewUrl
   */
  const handlePublishSuccess = useCallback(
    (result: { id: string; rule_id?: number; rule_key?: string; url?: string; }): void => {
      const url = result?.url || '';
      const rawUrl = url.trim() || previewUrl;
      if (!rawUrl.trim()) return;
      // 相对路径补全为完整链接
      const fullUrl = /^https?:\/\//.test(rawUrl)
        ? rawUrl
        : `${window.location.origin}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
      setPublishedUrl(fullUrl);
      setShareLinkModalVisible(true);
    },
    [previewUrl],
  );

  /** 关闭浏览链接弹窗 */
  const closeShareLinkModal = useCallback((): void => {
    setShareLinkModalVisible(false);
  }, []);

  return {
    stepActive,
    setStepActive,
    previewUrl,
    htmlSource,
    displayTitle,
    toolbarDisabled,
    isLoading,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleDownload,
    handleCopyClipboard,
    showCopyTips,
    handlePublish,
    publishModalVisible,
    closePublishModal,
    handleConfirmPublish,
    shareLinkModalVisible,
    publishedUrl,
    handlePublishSuccess,
    closeShareLinkModal,
  };
}
